import { resources, supportedLanguages } from "config/resources";
import i18n, { FormatFunction } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import moment from "moment";
import { initReactI18next } from "react-i18next";
import { CurrencyDisplay, getCurrency, setCurrencyDisplay } from "translations/currency";
import { getSearchParams } from "search-params";

// Import all moment locales
import "moment/dist/locale/da"; // Danish
import "moment/dist/locale/es"; // Spanish
import "moment/dist/locale/fi"; // Finnish
import "moment/dist/locale/nb"; // Norwegian
import "moment/dist/locale/nl"; // Dutch
import "moment/dist/locale/sv"; // Swedish
import "moment/dist/locale/et"; // Estonian
import "moment/dist/locale/lt"; // Lithuanian
import "moment/dist/locale/lv"; // Latvian
import { warn } from "logger/logger";

const countryCodeToLangaugeMap: Record<string, string> = {
    no: "nb",
    se: "sv",
    dk: "da",
    fi: "fi",
    es: "es",
    nl: "nl",
    be: "fr",
    ee: "et",
    ru: "ru",
    lt: "lt",
    lv: "lv",
};

const { system } = getSearchParams();
const systemPrefix = system?.split("_")[0];
const originalLanguage = systemPrefix ? countryCodeToLangaugeMap[systemPrefix] || "sv" : "sv";
const currency = getCurrency(system || "");
const userLocale = navigator.language;

interface CustomFormatParams {
    display?: CurrencyDisplay;
}
/**
 * Runs when accessing a translation key that implements string interpolation
 */
const customFormat: FormatFunction = (value: unknown, format, _, options) => {
    const params = options as CustomFormatParams;
    /* Check which format parameter that is passed to the function */
    const detectedLanguage = i18n.language;
    const isOriginalLanguage = originalLanguage === detectedLanguage;
    if (format === "currency") {
        const price = Number(value);
        const hasDecimal = price % 1 !== 0;
        /* If the format is set to currency - format the value with the Intl.NumberFormat based on the users locale */
        const formattedCurrency = new Intl.NumberFormat(userLocale, {
            style: "currency",
            currency: currency,
            maximumFractionDigits: hasDecimal ? 2 : 0,
            currencyDisplay: setCurrencyDisplay(
                params.display ?? CurrencyDisplay.Symbol,
                isOriginalLanguage
            ),
        }).format(Number(value));
        /* If the display is none - trim of the last 3 ISO letters */
        if (params.display === CurrencyDisplay.None) {
            return formattedCurrency.replace(/[a-zA-Z]/g, "").trim();
        }
        /* return formated value */
        return formattedCurrency;
    }

    if (format === "time" || format === "date") {
        // Value is suppose to be Unix Epoch
        if (typeof value === "string" || typeof value === "number") {
            const numericValue = Number(value);
            // Check if this is likely a Unix timestamp (seconds) vs JavaScript timestamp (milliseconds)
            // Convert to milliseconds if it's a Unix timestamp
            const milliseconds = numericValue < 100000000000 ? numericValue * 1000 : numericValue;

            const options: Intl.DateTimeFormatOptions =
                format === "time" ? { timeStyle: "short" } : { month: "short", day: "numeric" };
            const formatter = new Intl.DateTimeFormat(userLocale, options);
            return formatter.format(milliseconds);
        } else if (value === undefined) {
            return "undefined";
        } else {
            warn("incorrect type to time localization, use a string");
            return value as string;
        }
    }
    return value as string;
};

void i18n
    .use(initReactI18next)
    .use(HttpApi)
    .use(LanguageDetector)
    .init({
        detection: {
            order: ["querystring", "localStorage", "navigator"],
            lookupQuerystring: "lang",
        },
        nonExplicitSupportedLngs: true,
        supportedLngs: supportedLanguages,
        fallbackLng: ["en", "sv"],
        debug: false,
        interpolation: {
            formatSeparator: ",",
            format: customFormat,
            escapeValue: false,
        },
        resources,
    })
    .then(() => {
        moment.locale(i18n.language);
    });

// Listen for changes in i18n language and update moment's locale accordingly
i18n.on("languageChanged", function (lng) {
    moment.locale(lng);
});
